import { createContext, useContext, type ReactNode } from "react";
import type {
  Diagnostic,
  CategoryId,
  EdgeDirection,
  NodeMetadata,
  Warning,
  DeployBlockInfo,
  NodeDiffMeta,
  SystemNode,
  FacetOverviewEntry,
} from "@karasu-tools/core";
import type { ActiveView } from "./app-reducer.js";
import type { SharePayload } from "../utils/inline-share.js";

/**
 * System-view grouping axis. `"team"` groups by owning team (#1858, P2a);
 * `"boundary"` groups by declared `boundary` block (#1822, P2b).
 */
export type GroupByMode = "none" | "team" | "boundary";

/**
 * The core grouping axis for a Group-by view-state value: `"none"` means no
 * axis, any other mode passes through unchanged. The single conversion point
 * for every surface that forwards the selector's value into core (`compile`
 * options, the export builders and entity view via `useViewSvg`), so a new
 * axis added to {@link GroupByMode} flows through without editing call sites —
 * a per-site hardcode (`=== "team" ? "team" : undefined`) silently dropped the
 * boundary axis from the export surfaces (#2033, TPL-219).
 */
export function groupByAxis(groupBy: GroupByMode): Exclude<GroupByMode, "none"> | undefined {
  return groupBy === "none" ? undefined : groupBy;
}

interface SystemViewData {
  svg: string;
  diagnostics: Diagnostic[];
  viewPath: string[];
  breadcrumbItems: { id: string; label: string }[];
  warnings: Warning[];
  onBreadcrumbNavigate: (path: string[]) => void;
  /** Called when user clicks the deploy button on a service node */
  onDeployButtonClick?: (serviceId: string) => void;
  /** Called when user clicks the team label on a service/domain node */
  onTeamButtonClick?: (teamId: string) => void;
  /** Called when user clicks a category control to collapse/expand it (#1821). */
  onCategoryToggle?: (category: CategoryId) => void;
  /** Called when the user clicks a group ⊖/⊕ control to collapse/expand a team (#1858). */
  onGroupToggle?: (groupId: string) => void;
  /** Called when the user clicks a ⊕/⊖ in-place expansion control on a service (#1921). */
  onExpandToggle?: (serviceId: string) => void;
  /** Current system-view grouping axis (#1858). */
  groupBy?: GroupByMode;
  /** Called when the user changes the Group-by selector (#1858). */
  onGroupByChange?: (mode: GroupByMode) => void;
  /** Facets selected for the overlay (#2174). Orthogonal to `groupBy` — both apply at once. */
  selectedFacets?: readonly string[];
  /** Called when the user toggles a facet in the Facets selector (#2174). */
  onFacetToggle?: (facetId: string) => void;
  /** Every facet the model knows — the selector's options. Empty hides the control. */
  facets?: { id: string; label?: string }[];
  /**
   * Derived "which elements belong to facet X" (#2177). Same list, same order
   * as `facets` — the overview is a second *view* of one derivation, not a
   * second source (TPL-1032).
   */
  facetOverview?: FacetOverviewEntry[];
  /** Whether the team axis has data — gates the "team" option (#1822 P2b). */
  hasTeamAxis?: boolean;
  /** Whether the boundary axis has data — gates the "boundary" option (#1822 P2b). */
  hasBoundaryAxis?: boolean;
  /** Whether anything (a team frame or external/infra band) is collapsible (#1872). */
  anyCollapsible?: boolean;
  /** True when every team frame AND external/infra category is collapsed (#1872). */
  allCollapsed?: boolean;
  expansionOverload?: boolean;
  /** Collapse everything (frames + categories) if anything is open, else expand all (#1872). */
  onCollapseAllToggle?: () => void;
  highlightedNodeId?: string | null;
  onClearHighlight?: () => void;
  /**
   * Per-node diff metadata when diff mode is active. Undefined outside
   * diff mode. Used by the detail panel to surface annotation diffs
   * (Issue #738 / design doc D-2).
   */
  nodeDiff?: Map<string, NodeDiffMeta>;
  /** Resolved system AST — used by the CRUD matrix view. */
  systems: SystemNode[];
}

interface DeployViewData {
  svg: string;
  diagnostics: Diagnostic[];
  warnings: Warning[];
  highlightedNodeId?: string | null;
  onClearHighlight?: () => void;
  onContainerClick?: (containerId: string) => void;
}

interface OrgViewData {
  svg: string;
  diagnostics: Diagnostic[];
  viewPath: string[];
  breadcrumbItems: { id: string; label: string }[];
  warnings: Warning[];
  onBreadcrumbNavigate: (path: string[]) => void;
  highlightedNodeId?: string | null;
  onClearHighlight?: () => void;
  /** Called when user clicks an owned service link on a team node to cross-navigate to system view */
  onOwnedServiceClick?: (serviceId: string) => void;
}

export interface PreviewContextValue {
  activeView: ActiveView;
  hasDeployDiagram: boolean;
  onActiveViewChange: (view: ActiveView) => void;

  systemView: SystemViewData;
  deployView: DeployViewData;
  orgView: OrgViewData;

  nodeMetadata: Map<string, NodeMetadata>;

  deployBlocks?: DeployBlockInfo[];
  selectedDeployBlockId?: string | null;
  onDeployBlockChange?: (id: string) => void;

  onExportSvg: (svg: string, filename: string) => void;
  /**
   * Whether the active file has shareable source — drives the Share button's
   * enabled state. Kept as a boolean (not the source string) so it only flips
   * when crossing empty↔non-empty, never on every keystroke.
   */
  hasKrsSource?: boolean;
  /**
   * Flatten the current project into a self-contained share payload (a single
   * `.krs` + merged `.krs.style`) for an inline share URL (karasu-nest). Async
   * because multi-file projects are resolved from the filesystem. Stable
   * identity, so threading it through the preview memo doesn't re-render the
   * preview on every keystroke.
   */
  getShareBundle?: () => Promise<SharePayload>;
  /**
   * Export the current project as a draw.io (mxGraph XML) file. Absent when
   * the active shell has no project available (e.g. editor-only modes).
   */
  onExportDrawio?: (filename: string) => Promise<void>;

  isAllLayersOpen: boolean;
  onAllLayersToggle: () => void;
  /** SVG with all system levels stacked vertically (Show All Layers on system tab) */
  allLayersSvg?: string;
  /** SVG with all org levels stacked vertically (Show All Layers on org tab) */
  orgAllLayersSvg?: string;
  /** SVG with CSS :target navigation for system drill-down export */
  drillDownSvg?: string;
  /** SVG with CSS :target navigation for org drill-down export */
  orgDrillDownSvg?: string;
  /** Bundled SVG with all views (system/deploy/org) and CSS-only tab navigation */
  allViewsSvg?: string;

  previewFocused: boolean;
  onPreviewFocusToggle: () => void;
  /** Called when user clicks "Jump to editor" in the detail panel */
  onJumpToEditor?: (nodeId: string) => void;

  isOrgTreeViewOpen: boolean;
  onOrgTreeViewToggle: () => void;
  /** Rendered SVG for the current Org Tree expand state */
  orgTreeSvg?: string;
  /** Called when a team node is clicked in the tree view */
  onTeamToggle?: (teamId: string) => void;
  /** Fully-expanded org tree SVG for export */
  orgTreeExportSvg?: string;

  /** Org tab third mode (#2636): the derived team-dependency graph. */
  isTeamDependenciesOpen: boolean;
  onTeamDependenciesToggle: () => void;
  /** Rendered SVG for the derived team dependencies. */
  teamDependencySvg?: string;
  /** Whether the model declares an organization at all (gates the toggle). */
  hasTeamDependencyView: boolean;

  /** Entity sub-mode: show the drilled domain's entity view (mirrors Org Tree View). */
  isEntityViewOpen: boolean;
  onEntityViewToggle: () => void;
  /** Rendered entity view SVG for the currently drilled domain. */
  entityViewSvg?: string;
  /** Whether the drilled domain has an entity view (gates the toggle). */
  hasEntityView: boolean;

  /**
   * Path of the `.krs.style` file the GUI append writer should target,
   * resolved from the active project (typically the last `@import` in the
   * current `.krs`). `undefined` keeps the right-click → Direction menu
   * disabled with a hint.
   */
  styleTargetPath?: string;
  /**
   * Apply a GUI-driven edge `direction` override by appending an
   * `edge#<canonicalId> { direction: <value>; }` rule to the active
   * `.krs.style` file. `undefined` when the host shell does not support
   * GUI style writes.
   */
  onPickEdgeDirection?: (canonicalId: string, direction: EdgeDirection) => void;
}

const PreviewContext = createContext<PreviewContextValue | null>(null);

/**
 * Provides the view state + handlers consumed by `PreviewColumn` and its
 * children. AppShell is the sole producer — mode apps don't need to touch it.
 */
export function PreviewProvider({
  value,
  children,
}: {
  value: PreviewContextValue;
  children: ReactNode;
}) {
  return <PreviewContext.Provider value={value}>{children}</PreviewContext.Provider>;
}

export function usePreview(): PreviewContextValue {
  const ctx = useContext(PreviewContext);
  if (!ctx) {
    throw new Error("usePreview must be used within a PreviewProvider");
  }
  return ctx;
}
